import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { type InvoiceRange, type Locale } from '@worki/shared';
import { formatDate, monthName } from '../lib/format.js';
import { cn } from '../lib/cn.js';
import { AnchoredMenu } from './AnchoredMenu.js';

/**
 * INV-006 — when, as one control.
 *
 * It used to be three dropdowns standing in a row: a period, a month, and a
 * year, with the first two swapping places depending on whether a year was
 * chosen. Three controls for one question, and the answer was spread across
 * them — nothing on the screen said "March 2026", it said "March" beside
 * "2026" beside a period box that had quietly disappeared.
 *
 * So the question is asked once. The trigger states the answer in the words
 * someone would use for it, and everything that can change it lives in one
 * panel: the years the archive can reach, the months inside a chosen year, and
 * two dates for a span that is neither.
 *
 * The three are exclusive, and the panel enforces that rather than leaving two
 * of them on screen to contradict each other. A hand-typed span has no use for
 * a year, and a month with no year behind it names no month in particular.
 *
 * A row of running periods — this week, this month, the last two, this year —
 * used to sit above the years, and was taken out: it answered the same question
 * the years already answer, and cost a third of the panel to do it. `range`
 * stays on the shape and on the query, holding its default, because the server
 * still filters by it and a future control may want it back.
 */

export interface Period {
  range: InvoiceRange;
  /** '' or '1'..'12'. Only meaningful with at least one year. */
  month: string;
  /**
   * The four-digit years picked, in ascending order. Empty is every year.
   *
   * A list rather than one year, because the questions an archive is opened
   * with are often plural — what a supplier has cost over three years, or two
   * years compared — and asking them one year at a time meant adding the
   * totals up by hand outside the screen.
   */
  years: string[];
  /** '' or `YYYY-MM-DD` — the ends of a span picked by hand. */
  from: string;
  to: string;
}

interface PeriodPickerProps {
  value: Period;
  onChange: (next: Period) => void;
  /** The years the archive can reach, newest first. Not the chosen ones. */
  available: number[];
  /**
   * The first day the archive shows, as `YYYY-MM-DD`. The hand-picked dates
   * stop there: a day before it opens on a list the server will not fill.
   */
  earliest?: string;
  locale: Locale;
  className?: string;
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Past this many, the trigger counts them instead of naming them. */
const NAMED_YEARS = 2;

export function PeriodPicker({ value, onChange, available, earliest, locale, className }: PeriodPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const custom = Boolean(value.from || value.to);

  /**
   * What the control says it is showing.
   *
   * The order is the order the parts outrank each other on the server, so the
   * label can never name a period the list is not actually filtered by.
   */
  const label = (() => {
    if (custom) {
      const from = value.from ? formatDate(`${value.from}T00:00:00`, locale) : null;
      const to = value.to ? formatDate(`${value.to}T00:00:00`, locale) : null;
      if (from && to) return `${from} – ${to}`;
      return from ? t('invoices.period.fromDay', { day: from }) : t('invoices.period.untilDay', { day: to });
    }
    if (value.years.length) {
      // Two are worth naming; four read as a number of years rather than as
      // four separate answers, and the trigger has one line for all of it.
      const named =
        value.years.length <= NAMED_YEARS
          ? value.years.join(', ')
          : t('invoices.period.yearsCount', { count: value.years.length });
      return value.month ? `${monthName(Number(value.month), locale)} ${named}` : named;
    }
    return t(`invoices.range.${value.range}`);
  })();

  /*
   * The range is left alone when a year is chosen, and that is deliberate: the
   * year outranks it while it is set, so clearing the year puts back the period
   * that was showing before rather than dropping the list to some default the
   * person never picked.
   */
  const toggleYear = (year: string) => {
    const next = value.years.includes(year)
      ? value.years.filter((chosen) => chosen !== year)
      : [...value.years, year].sort();
    // A month with no year behind it means "whichever of these has already
    // been", which is not what closing 2024's books wants. Unticking the last
    // year therefore takes the month with it.
    onChange({ ...value, years: next, month: next.length ? value.month : '', from: '', to: '' });
  };

  const everyYear = () => onChange({ ...value, years: [], month: '', from: '', to: '' });

  /*
   * The panel stays open after a month, as it already does after a year. It
   * used to close on the month, which made "January, then February" two
   * openings for one comparison; the click outside is what says someone is
   * done, and the menu already closes on that.
   */
  const pickMonth = (month: string) => {
    onChange({ ...value, month, from: '', to: '' });
  };

  const pickDay = (edge: 'from' | 'to', day: string) => {
    onChange({ ...value, [edge]: day, years: [], month: '' });
  };

  const chip = (active: boolean) =>
    cn(
      'h-8 px-3 rounded-lg text-xs font-medium transition-colors shrink-0',
      active ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-accent text-foreground',
    );

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-label={t('invoices.rangeLabel')}
        className={cn(
          'h-9 px-3 flex items-center gap-2 rounded-lg text-sm shrink-0 max-w-[60%]',
          'bg-muted outline-none transition-colors cursor-pointer',
          'hover:bg-muted-foreground/[0.16] focus-visible:ring-2 focus-visible:ring-primary/40',
          open && 'ring-2 ring-primary/40',
          className,
        )}
      >
        <CalendarDays className="h-4 w-4 text-primary shrink-0" />
        <span className="truncate">{label}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {/* Portalled like every other menu in the product: this page is one
          `animate-fade-up` block whose held transform makes it a stacking
          context, and it scrolls inside `page-container`. Either alone would
          swallow the panel. */}
      <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={triggerRef} width={288}>
        <div className="p-3 space-y-3 max-h-[min(28rem,70vh)] overflow-y-auto">
          <section className="space-y-1.5">
            <p className="text-[11px] font-semibold text-muted-foreground">{t('invoices.pickYear')}</p>
            {/* Each year is a switch, so picking a second one keeps the first
                — `aria-pressed` says so, because a chip that toggles and a
                chip that chooses look identical. */}
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={everyYear}
                aria-pressed={!custom && value.years.length === 0}
                className={chip(!custom && value.years.length === 0)}
              >
                {t('invoices.allYears')}
              </button>
              {available.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleYear(String(option))}
                  aria-pressed={!custom && value.years.includes(String(option))}
                  className={cn(chip(!custom && value.years.includes(String(option))), 'ltr-nums')}
                >
                  {option}
                </button>
              ))}
            </div>
          </section>

          {/* The months belong to a year, so they appear with one and not
              before: a month on its own is a different question, and answering
              it here would file last December under this one. */}
          {value.years.length > 0 && !custom && (
            <section className="space-y-1.5 border-t border-border pt-3">
              <p className="text-[11px] font-semibold text-muted-foreground">{t('invoices.pickMonth')}</p>
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  onClick={() => pickMonth('')}
                  className={cn(chip(value.month === ''), 'col-span-3 w-full')}
                >
                  {t('invoices.anyMonth')}
                </button>
                {MONTHS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => pickMonth(String(option))}
                    className={cn(chip(value.month === String(option)), 'w-full px-1 truncate')}
                  >
                    {monthName(option, locale)}
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="space-y-1.5 border-t border-border pt-3">
            <p className="text-[11px] font-semibold text-muted-foreground">{t('invoices.period.custom')}</p>
            <div className="flex items-center gap-1.5">
              {/*
                Native date inputs rather than a calendar of our own: the
                platform's picker is the one the phone already knows how to
                open, it is reachable from the keyboard, and it speaks the
                reader's own date order without being told.
              */}
              <input
                type="date"
                value={value.from}
                min={earliest}
                max={value.to || undefined}
                aria-label={t('invoices.period.fromLabel')}
                onChange={(event) => pickDay('from', event.target.value)}
                className="h-8 min-w-0 flex-1 px-2 rounded-lg bg-muted text-xs ltr-nums outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
              <span className="text-xs text-muted-foreground shrink-0">–</span>
              <input
                type="date"
                value={value.to}
                min={value.from || earliest}
                aria-label={t('invoices.period.toLabel')}
                onChange={(event) => pickDay('to', event.target.value)}
                className="h-8 min-w-0 flex-1 px-2 rounded-lg bg-muted text-xs ltr-nums outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
            </div>
            {custom && (
              <button
                type="button"
                onClick={() => onChange({ ...value, from: '', to: '' })}
                className="text-[11px] text-primary hover:underline"
              >
                {t('invoices.period.clearCustom')}
              </button>
            )}
          </section>
        </div>
      </AnchoredMenu>
    </>
  );
}
